import React, { useState } from "react";
import { Send } from "lucide-react";
import { api, formatError } from "../api";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { usePageContent, resolveMediaUrl } from "../components/PageContent";

const CATEGORIES = [
  { id: "story-submission", label: "Got a story you want us to publish" },
  { id: "event-idea", label: "Got an event idea (a bad one ideally)" },
  { id: "media-submission", label: "Got photos or video to submit" },
  { id: "general", label: "Just have a general complaint" },
];

export default function Complain() {
  const [form, setForm] = useState({ name: "", email: "", category: "general", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const c = usePageContent("complain", {
    eyebrow: "Filing Counter",
    title: "Complain Here",
    body: "Story submissions, event ideas, photo dumps, general grievances. Pick a category, tell us your name, don't lie.",
  });
  const bg = resolveMediaUrl(c.image);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact/submit", form);
      setSubmitted(true);
      toast.success("Complaint received. Have a nice day.");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Could not submit.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-bone min-h-screen" data-testid="complain-page">
      <SEO
        title={c.title || "Complain Here"}
        description={c.body}
        image={bg}
        path="/complain"
      />
      <section
        className="bg-asphalt text-bone py-16 relative overflow-hidden"
        style={
          c.show_media && bg
            ? { backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" }
            : undefined
        }
        data-testid="complain-hero"
      >
        {(!bg || !c.show_media) && <div className="absolute inset-0 halftone opacity-5 pointer-events-none" />}
        {c.show_media && bg && c.overlay && (
          <div className="absolute inset-0 bg-asphalt/80 mix-blend-multiply pointer-events-none" />
        )}
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10 relative">
          {c.eyebrow && <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-3">✦ {c.eyebrow}</div>}
          <h1 className="font-display text-6xl sm:text-7xl uppercase leading-none tracking-tighter whitespace-pre-line">{c.title}</h1>
          {c.subtitle && <p className="font-western text-2xl text-bone/85 mt-4 max-w-2xl">{c.subtitle}</p>}
          {c.body && <p className="font-western text-2xl text-bone/85 mt-4 max-w-2xl whitespace-pre-line">{c.body}</p>}
        </div>
      </section>
      <div className="tape-divider" />
      <section className="max-w-[1100px] mx-auto px-5 lg:px-10 py-16">
        {submitted ? (
          <div className="border-4 border-asphalt bg-card-light shadow-hard-lg p-10 text-center" data-testid="complain-success">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-terra mb-3">✦ Complaint Filed</div>
            <h2 className="font-display text-4xl uppercase tracking-tighter mb-3">Received, Confirmed, Forgotten.</h2>
            <p className="font-western text-xl text-ink">
              We'll read it. We promise. Well, somebody will. Probably.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="mt-6 border-2 border-asphalt px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] shadow-hard hover:bg-asphalt hover:text-bone"
            >
              File another
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="complain-form"
            className="border-4 border-asphalt bg-card-light shadow-hard-lg p-8 grid gap-6"
          >
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.3em] mb-2 block">What's this regarding?</label>
              <div className="grid sm:grid-cols-2 gap-3">
                {CATEGORIES.map((c) => (
                  <label
                    key={c.id}
                    className={`block border-2 border-asphalt p-4 cursor-pointer transition-colors ${
                      form.category === c.id ? "bg-asphalt text-bone" : "bg-bone hover:bg-card-light"
                    }`}
                  >
                    <input
                      type="radio"
                      name="category"
                      value={c.id}
                      checked={form.category === c.id}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="sr-only"
                      data-testid={`complain-category-${c.id}`}
                    />
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-70">{c.id}</div>
                    <div className="font-display text-lg uppercase tracking-tight leading-tight mt-1">{c.label}</div>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-xs uppercase tracking-[0.3em] mb-2 block">Your name</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="complain-name"
                  className="w-full px-4 py-3 border-2 border-asphalt bg-bone font-body focus:outline-none focus:border-terra"
                />
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-[0.3em] mb-2 block">Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="complain-email"
                  className="w-full px-4 py-3 border-2 border-asphalt bg-bone font-body focus:outline-none focus:border-terra"
                />
              </div>
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.3em] mb-2 block">Subject (optional)</label>
              <input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                data-testid="complain-subject"
                className="w-full px-4 py-3 border-2 border-asphalt bg-bone font-body focus:outline-none focus:border-terra"
              />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.3em] mb-2 block">Tell us the story</label>
              <textarea
                required
                rows={7}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="complain-message"
                className="w-full px-4 py-3 border-2 border-asphalt bg-bone font-body focus:outline-none focus:border-terra resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="complain-submit"
              className="inline-flex justify-center items-center gap-2 bg-terra text-bone border-2 border-asphalt px-7 py-4 font-display tracking-wider uppercase text-xl shadow-hard hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_#1C1B1A] transition-all disabled:opacity-60"
            >
              <Send className="w-5 h-5" /> {loading ? "Filing complaint…" : "File Complaint"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
